import { pageAsset } from "@Utils/getStaticAssetPath";
import { useEffect, useRef } from "react";
import Image from "@Components/Image";
import Reveal from "@Components/Reveal/Reveal";
import styles from "./GroupBookingSteps.module.scss";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";

// Images come from the static-assets bucket, never `import`ed — see `pageAsset`.
const asset = pageAsset("GroupBookingV2");
const stepsPhoto = asset("steps-photo.jpg");

/**
 * Everything the section renders comes from here, so a copy change is a one-place
 * edit and adding or dropping a step needs no other changes — the numbers are
 * derived from the index and the rail from the chips' measured positions.
 *
 * Curly quotes and a true em dash rather than the straight `"` and `-` of the
 * source doc — the heading already sets ’, and mixing the two on one page reads as
 * an oversight.
 */
const HEADING = "groupbooking.v2.steps.heading";

const STEPS = [
  {
    title: "groupbooking.v2.steps.step1.title",
    body: "groupbooking.v2.steps.step1.body",
  },
  {
    title: "groupbooking.v2.steps.step2.title",
    body: "groupbooking.v2.steps.step2.body",
  },
  {
    title: "groupbooking.v2.steps.step3.title",
    body: "groupbooking.v2.steps.step3.body",
  },
];

// Where down the viewport the fill edge sits, as a fraction of viewport height.
// Same value as HowItWorksV2's rail.
const FILL_LINE = 0.55;

/**
 * Sums an element's layout offset up to an ancestor.
 *
 * Deliberately uses offsetTop rather than getBoundingClientRect: every row is
 * wrapped in Reveal, which holds a translateY until the row scrolls into view.
 * Rects include that transform, so measuring them would place the rail against
 * wherever the rows happen to be mid-animation. offsetTop is pure layout, so the
 * rail lands on the chips' settled positions from the start.
 */
const offsetTopWithin = (el: HTMLElement, ancestor: HTMLElement): number => {
  let y = 0;
  let node: HTMLElement | null = el;
  while (node && node !== ancestor) {
    y += node.offsetTop;
    node = node.offsetParent as HTMLElement | null;
  }
  return y;
};

/**
 * How it works — HowItWorksV2's vertical stepper, on this page's grey band.
 *
 * A rail connects the numbered chips: a faint track with a bright fill that grows
 * as you scroll, so the line doubles as a progress indicator and each chip lights
 * as the fill reaches it. The rail spans chip-1-centre to chip-3-centre, measured
 * from layout, and the chips paint over it so it reads as passing behind them.
 *
 * Same values as that page, not an inversion of them: this band was graphite and has
 * been made grey, so the near-black fill on a grey track and the white chip that
 * fills dark when reached are shared with it again. See the palette block at the top
 * of the stylesheet.
 *
 * Media sits left of the stepper in a reserved slot. HowItWorksV2 pairs a clip
 * with every row; this is one slot for the whole section, held empty until there's
 * art for it — the space is what keeps the stepper where it will finally sit.
 *
 * Without JS the track still connects the chips; only the fill needs script.
 * Reduced-motion users get the rail filled solid, no scroll tracking.
 */
const Steps = () => {
  const { t } = useTranslationHook();
  const rowsRef = useRef<HTMLUListElement>(null);
  const railRef = useRef<HTMLSpanElement>(null);
  const fillRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const rows = rowsRef.current;
    const rail = railRef.current;
    const fill = fillRef.current;
    if (!rows || !rail || !fill) return undefined;

    const reduced =
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    // Rail geometry, in px from the top of the rows container.
    let chips: HTMLElement[] = [];
    let chipCentres: number[] = [];
    let railTop = 0;
    let railHeight = 0;
    // How many chips are currently lit. The lit set is always a prefix of the rail,
    // so this one number identifies it — used to skip redundant class writes on
    // every scroll frame.
    let lit = -1;

    const measure = () => {
      chips = Array.from(rows.querySelectorAll<HTMLElement>("[data-step-chip]"));
      if (chips.length < 2) {
        railHeight = 0;
        return;
      }
      chipCentres = chips.map((chip) => offsetTopWithin(chip, rows) + chip.offsetHeight / 2);
      [railTop] = chipCentres;
      railHeight = chipCentres[chipCentres.length - 1] - railTop;

      rail.style.top = `${railTop}px`;
      rail.style.bottom = "auto";
      rail.style.height = `${Math.max(0, railHeight)}px`;
    };

    const paintChips = (count: number) => {
      if (count === lit) return;
      lit = count;
      chips.forEach((chip, i) => {
        chip.classList.toggle(styles.markerActive, i < count);
      });
    };

    const update = () => {
      if (railHeight <= 0) return;
      // The fill edge tracks a fixed line down the viewport, so the bright part
      // covers every chip already scrolled past it.
      const railTopInViewport = rows.getBoundingClientRect().top + railTop;
      const line = window.innerHeight * FILL_LINE;
      const progress = Math.min(1, Math.max(0, (line - railTopInViewport) / railHeight));
      fill.style.transform = `scaleY(${progress.toFixed(4)})`;

      // Nothing is lit until the fill actually starts, otherwise chip 1 — whose
      // centre IS the rail's top — would read as reached before you get there.
      const edge = railTop + progress * railHeight;
      paintChips(progress > 0 ? chipCentres.filter((c) => c <= edge + 0.5).length : 0);
    };

    measure();

    if (reduced) {
      // No scroll tracking: show the journey complete.
      fill.style.transform = "scaleY(1)";
      paintChips(chips.length);
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }

    let raf = 0;
    const onScroll = () => {
      if (!raf) {
        raf = window.requestAnimationFrame(() => {
          raf = 0;
          update();
        });
      }
    };
    const onResize = () => {
      measure();
      update();
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, []);

  // No data-nav-theme: the band is grey now, so the navbar keeps its dark logo and
  // dark links over it. That attribute is the opt-in the shared Navbar reads to go
  // white (CareerV2's CrewCTA and ScholarshipV2's Categories still use it) —
  // left on a light band it would make the header invisible.
  return (
    <section className={styles.section}>
      <Reveal as="h2" className={styles.heading}>
        {t(HEADING)}
      </Reveal>

      <div className={styles.layout}>
        {/* The slot the layout was holding open. It kept its 4:3 while it was empty so
            the stepper beside it sat at its final height — the photo is cropped to that
            same ratio, so nothing moved when it landed. */}
        <div className={styles.mediaSlot}>
          <Image
            src={stepsPhoto}
            alt={t("groupbooking.v2.steps.photo.alt")}
            className={styles.mediaImage}
            width={1400}
            height={1050}
            // Without this the shared Image component ships `src=""` and waits for its
            // lazy-load observer, which never fired here — the slot stayed empty grey
            // and looked exactly like the placeholder it replaced. Every other photo on
            // this page passes it for the same reason.
            isNotLazy
          />
        </div>

        <ul ref={rowsRef} className={styles.rows}>
          {/* Connector rail. Sits behind the chips; geometry set by the effect. */}
          <span ref={railRef} className={styles.rail} aria-hidden="true">
            <span ref={fillRef} className={styles.railFill} />
          </span>

          {STEPS.map((step, index) => (
            <Reveal as="li" key={step.title} className={styles.row} delay={index * 70}>
              {/* Derived, not written down, so reordering or inserting a step can't
                  leave the numbering stale. */}
              <span className={styles.marker} data-step-chip>
                {index + 1}
              </span>
              <h3 className={styles.title}>{t(step.title)}</h3>
              <p className={styles.body}>{t(step.body)}</p>
            </Reveal>
          ))}
        </ul>
      </div>
    </section>
  );
};

export default wrapperHOC(Steps, {
  componentName: "Steps-GroupBookingV2",
  showForChina: true,
});
